import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AdminRoleContextType {
  isAdmin: boolean;
  loading: boolean;
}

const AdminRoleContext = createContext<AdminRoleContextType>({ isAdmin: false, loading: true });

export const useAdminRole = () => useContext(AdminRoleContext);

/**
 * Guard helper for admin-only pages. Returns { isAdmin, loading }.
 * Callers should render a spinner while `loading`, and redirect when `!loading && !isAdmin`.
 */
export const useRequireAdmin = () => {
  const { isAdmin, loading } = useContext(AdminRoleContext);
  return { isAdmin, loading };
};

export const AdminRoleProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      try {
        const { data } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();
        setIsAdmin(!!data);
      } catch {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [user]);

  return (
    <AdminRoleContext.Provider value={{ isAdmin, loading }}>
      {children}
    </AdminRoleContext.Provider>
  );
};
